/**
 * Ingestion run page for both experiences. Internal callers also get a review section on failed
 * runs.
 */

import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import FormField from "@cloudscape-design/components/form-field"
import Header from "@cloudscape-design/components/header"
import Input from "@cloudscape-design/components/input"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import StatusIndicator from "@cloudscape-design/components/status-indicator"
import Textarea from "@cloudscape-design/components/textarea"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import type { ReviewStatus } from "@/generated/sdk"
import Breadcrumb from "@/components/Navigation/Breadcrumb"
import RunDetailBody from "@/components/RunDetailModal/RunDetailBody"
import { useRunDetail, runDetailKeys } from "@/components/RunDetailModal/useRunDetail"
import { ingestionKeys } from "@/hooks/queries/useIngestion"
import { useMyAccess } from "@/hooks/queries/useMyAccess"
import { ingestionApiService } from "@/services/ingestionApi"

const STATUS_OPTIONS = [
  { label: "Pending Review", value: "PENDING_REVIEW" },
  { label: "Acknowledged", value: "ACKNOWLEDGED" },
  { label: "Resolved", value: "RESOLVED" },
  { label: "Ignored", value: "IGNORED" },
]

/**
 * Review section for a failed run: saves status, assignee, and resolution notes, then refetches.
 */
const ReviewSection = ({ studyId, runId }: { studyId: string; runId: string }) => {
  const queryClient = useQueryClient()
  const { run } = useRunDetail(studyId, runId)

  const [reviewStatus, setReviewStatus] = useState<ReviewStatus>("PENDING_REVIEW")
  const [assignedTo, setAssignedTo] = useState("")
  const [resolution, setResolution] = useState("")
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Seed the form when the saved review fields load or change. Depend on the fields, not `run`,
  // whose identity changes on every poll and would clobber an in-progress edit.
  const runReviewStatus = run?.reviewStatus
  const runAssignedTo = run?.assignedTo
  const runResolution = run?.resolution
  useEffect(() => {
    if (!run) return
    setReviewStatus(runReviewStatus || "PENDING_REVIEW")
    setAssignedTo(runAssignedTo || "")
    setResolution(runResolution || "")
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed only when persisted fields change
  }, [runReviewStatus, runAssignedTo, runResolution])

  const hasChanges =
    reviewStatus !== run?.reviewStatus ||
    assignedTo !== (run?.assignedTo || "") ||
    resolution !== (run?.resolution || "")

  const handleSave = async () => {
    try {
      setSaving(true)
      setSaveError(null)
      setSaved(false)
      await ingestionApiService.updateRun(studyId, runId, {
        reviewStatus,
        assignedTo: assignedTo || undefined,
        resolution: resolution || undefined,
      })
      await queryClient.invalidateQueries({ queryKey: runDetailKeys.all(studyId, runId) })
      void queryClient.invalidateQueries({ queryKey: ingestionKeys.runs(studyId) })
      void queryClient.invalidateQueries({ queryKey: ingestionKeys.runMetrics(studyId) })
      setSaved(true)
    } catch (err) {
      console.error("Failed to update run:", err)
      setSaveError("Failed to save changes. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Container header={<Header variant="h2">Review</Header>}>
      <SpaceBetween size="m">
        {saveError && <Alert type="error">{saveError}</Alert>}
        {saved && !hasChanges && (
          <Alert type="success" dismissible onDismiss={() => setSaved(false)}>
            Review saved.
          </Alert>
        )}
        <FormField label="Status">
          <Select
            selectedOption={
              STATUS_OPTIONS.find((o) => o.value === reviewStatus) || STATUS_OPTIONS[0]
            }
            onChange={({ detail }) => setReviewStatus(detail.selectedOption.value as ReviewStatus)}
            options={STATUS_OPTIONS}
          />
        </FormField>
        <FormField label="Assigned To">
          <Input
            value={assignedTo}
            onChange={({ detail }) => setAssignedTo(detail.value)}
            placeholder="user@example.com"
          />
        </FormField>
        <FormField label="Resolution Notes">
          <Textarea
            value={resolution}
            onChange={({ detail }) => setResolution(detail.value)}
            placeholder="Describe how this issue was resolved..."
            rows={3}
          />
        </FormField>
        <Box float="right">
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={!hasChanges || saving}
            loading={saving}
          >
            Save Changes
          </Button>
        </Box>
      </SpaceBetween>
    </Container>
  )
}

/**
 * Run details page for the studyId and runId route parameters.
 */
export default function RunDetails() {
  const { studyId, runId } = useParams<{ studyId: string; runId: string }>()
  const navigate = useNavigate()
  const { isInternal: isDae } = useMyAccess()
  const { run, isLive } = useRunDetail(studyId ?? "", runId ?? "")

  if (!studyId || !runId) {
    return (
      <Box padding="l">
        <StatusIndicator type="error">Missing study or run identifier.</StatusIndicator>
      </Box>
    )
  }

  const shortRunId = runId.slice(0, 8)

  const breadcrumbItems = isDae
    ? [
        { label: "All Studies", path: "/studies" },
        { label: studyId, path: `/studies/${studyId}?tab=ingestion` },
        { label: `Run ${shortRunId}` },
      ]
    : [{ label: studyId }, { label: `Run ${shortRunId}` }]

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <SpaceBetween size="xs">
          <Breadcrumb items={breadcrumbItems} />
          <Header
            variant="h1"
            description={
              isLive ? (
                <StatusIndicator type="in-progress">Live Updates</StatusIndicator>
              ) : undefined
            }
            // Vendor breadcrumbs have no clickable trail, so link back to the submit page.
            actions={
              isDae ? undefined : (
                <Button iconName="arrow-left" onClick={() => navigate(-1)}>
                  Back to submission
                </Button>
              )
            }
          >
            Run {shortRunId}
          </Header>
        </SpaceBetween>

        <RunDetailBody studyId={studyId} runId={runId} />

        {isDae && run?.status === "FAILED" && <ReviewSection studyId={studyId} runId={runId} />}
      </SpaceBetween>
    </Box>
  )
}
